import React from 'react';
import appStyles from '../App.module.css';
import styles from './Pages.module.css';
import Wall from '../components/Wall';
import Tile from '../components/Tile';
import RsvpForm from '../components/RsvpForm';

/**
 * /meetup — the standing meetup page.
 * Cadence: every other Thursday, 6–8 PM (series started Thu Sep 10, 2026).
 * Each cycle, bump EVENT_ID / NEXT_DATE / FOLLOWING_DATE (and the matching
 * homepage tile + schema.org block in public/index.html). The calendar links
 * are recurring, so they only change if the cadence or venue does.
 */

const EVENT_ID = '2026-09-24';
const NEXT_DATE = 'Thu Sep 24';
const FOLLOWING_DATE = 'Thu Oct 8';

const GCAL_URL =
  'https://calendar.google.com/calendar/render?action=TEMPLATE' +
  '&text=Oak%20Cliff%20Tech%20Meetup' +
  '&dates=20260924T180000/20260924T200000&ctz=America/Chicago' +
  '&recur=RRULE%3AFREQ%3DWEEKLY%3BINTERVAL%3D2%3BBYDAY%3DTH' +
  '&location=Lola%27s%20Cantina%20%28at%20East%20Dock%29%2C%20900%20E.%20Clarendon%20Dr%2C%20Dallas%2C%20TX%2075203' +
  '&details=Show%20%26%20tell%20mixer%20%E2%80%94%20bring%20something%20you%27re%20working%20on.%20https%3A%2F%2Foakcliff.tech%2Fmeetup';

const DIRECTIONS_URL =
  'https://www.google.com/maps/dir/?api=1&destination=900+E+Clarendon+Dr+Dallas+TX+75203';

const Meetup: React.FC = () => (
  <Wall>
    {/* Page hero — span 4, surface */}
    <Tile span={4} tone="surface">
      <div className={styles.kicker}>◆ Every other Thursday. Show us what you&rsquo;ve got.</div>
      <h1 className={styles.slab}>Next<br />Meetup</h1>
      <p className={styles.lead}>
        <b>This one has a job to do: bring something you&rsquo;re working on.</b>{' '}
        An app, a side project, a sketchbook, a half-broken robot. No stage, no
        slides — just a table, a drink, and the thing you&rsquo;ve been building.
      </p>
    </Tile>

    {/* 01 / When & Where — span 2, ink */}
    <Tile span={2} tone="ink" label="01 / When & Where" glyph="◆">
      <div className={styles.bigDate}>
        {NEXT_DATE}<br />6&ndash;8 PM
      </div>
      <div className={styles.sub}>
        Every other Thursday &middot; next up {FOLLOWING_DATE}<br />
        Lola&rsquo;s Cantina at East Dock<br />900 E. Clarendon Dr, Dallas, TX 75203
      </div>
      <div className={styles.linkRow}>
        <a className={styles.linkOchre} href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer">
          ◆ Get directions
        </a>
        <a className={styles.linkOchre} href={GCAL_URL} target="_blank" rel="noopener noreferrer">
          + Google Calendar
        </a>
        <a className={styles.linkOchre} href="/oakcliff-tech-meetup.ics" download>
          + Apple / Outlook (.ics, recurring)
        </a>
      </div>
    </Tile>

    {/* 02 / The Format — span 3 */}
    <Tile span={3} label="02 / The Format" glyph="▸">
      <div className={appStyles.list}>
        <div className={appStyles.listItem}>
          <b>Show &amp; tell mixer</b>
          <span>Tables, drinks, and whatever your neighbors brought. Short welcome at 6:30 — the rest is unstructured on purpose.</span>
        </div>
        <div className={appStyles.listItem}>
          <b>Every other Thursday</b>
          <span>Same table, same time, every two weeks. Miss one, catch the next &mdash; add the series to your calendar and stop thinking about it.</span>
        </div>
        <div className={appStyles.listItem}>
          <b>First time? Good.</b>
          <span>No badges, no pitch, no sponsor decks. Walk in, grab a drink, find the tables with projects on them.</span>
        </div>
        <div className={appStyles.listItem}>
          <b>Not building anything?</b>
          <span>Come anyway. Lurkers, learners, and the tech-curious are all neighbors here.</span>
        </div>
      </div>
    </Tile>

    {/* 03 / Bring — span 3, olive */}
    <Tile span={3} tone="olive" label="03 / Bring" glyph="✦">
      <p className={styles.creamCopy}>
        <b>The thing you&rsquo;re working on</b> &middot; one person who builds
        things &middot; yourself. If it fits in a bag, it belongs on the table.
      </p>
    </Tile>

    {/* 04 / RSVP — span 3, ochre */}
    <Tile span={3} tone="ochre" label="04 / RSVP" glyph="◆">
      <RsvpForm event={EVENT_ID} />
    </Tile>

    {/* 05 / Spread It — span 3, brick */}
    <Tile span={3} tone="brick" label="05 / Spread It" glyph="▸">
      <p className={styles.creamCopy}>
        <b>Every yes fills the room.</b> Forward this to one person in the
        neighborhood who builds things — or just send them the link.
      </p>
      <div className={styles.linkRow}>
        <a className={styles.linkCream} href="/">
          ◂ Back to oakcliff.tech
        </a>
      </div>
    </Tile>
  </Wall>
);

export default Meetup;
